import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { PublicShell } from "@/components/shared";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Stack() {
  const { data, isLoading } = useQuery({ queryKey: ["stack"], queryFn: async () => (await api.get("/stack")).data });

  return (
    <PublicShell>
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald" data-testid="stack-overline">The New Stack</p>
          <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-navy sm:text-5xl" data-testid="stack-title">
            How I run a product business in 2026 — and what each of these replaced.
          </h1>
          <p className="mt-5 max-w-2xl rounded-lg border border-emerald/30 bg-emerald/5 p-4 text-[15px] leading-relaxed text-slate-700" data-testid="stack-disclosure">
            Some of these links pay me a commission. I only list what I actually use, and I've said where each one falls short. It costs you nothing and doesn't change what's on this page.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-14 lg:px-8" data-testid="stack-categories">
        {isLoading && <p className="text-slate-500">Loading…</p>}
        {!isLoading && (data?.count ?? 0) === 0 && (
          <p className="text-slate-500" data-testid="stack-empty">The first tools are being written up — check back shortly.</p>
        )}
        {(data?.categories || []).map((g) => g.tools.length > 0 && (
          <div key={g.category} className="mb-12" data-testid={`stack-cat-${g.category}`}>
            <h2 className="font-heading text-xl font-bold text-navy">{g.category}</h2>
            <div className="mt-4 space-y-4">
              {g.tools.map((t) => (
                <a key={t.id} href={`${API_URL}/api/stack/click/${t.id}`} target="_blank" rel="sponsored noopener"
                  data-testid={`stack-tool-${t.id}`}
                  className="group block rounded-xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-emerald/40 hover:shadow-[0_10px_30px_rgb(0,0,0,0.05)]">
                  {t.replaced && <p className="mb-2 text-sm text-slate-400" data-testid={`stack-replaced-${t.id}`}><span className="line-through decoration-red-400/70 decoration-2">{t.replaced}</span></p>}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-emerald">{t.name}</h3>
                      {t.is_affiliate && <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-testid={`stack-affiliate-${t.id}`}>affiliate</span>}
                    </div>
                    <ArrowUpRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition-colors group-hover:text-emerald" />
                  </div>
                  <p className="mt-1.5 text-sm font-medium text-slate-600">{t.what_it_does}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{t.honest_take}</p>
                </a>
              ))}
            </div>
          </div>
        ))}
      </section>
    </PublicShell>
  );
}
